import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { AUTH_LAUNCH_MESSAGES, authEase } from './authMotion';

interface AuthLaunchTransitionProps {
  active: boolean;
  onComplete?: () => void;
  messages?: readonly string[];
}

/** Cinematic post-auth launch — operational loading before dashboard redirect */
export const AuthLaunchTransition: React.FC<AuthLaunchTransitionProps> = ({
  active,
  onComplete,
  messages = AUTH_LAUNCH_MESSAGES,
}) => {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return undefined;
    }
    if (reduceMotion) {
      const t = window.setTimeout(() => onComplete?.(), 400);
      return () => window.clearTimeout(t);
    }
    let step = 0;
    const timer = window.setInterval(() => {
      step += 1;
      if (step >= messages.length) {
        window.clearInterval(timer);
        window.setTimeout(() => onComplete?.(), 480);
      } else {
        setIndex(step);
      }
    }, 1100);
    return () => window.clearInterval(timer);
  }, [active, messages.length, onComplete, reduceMotion]);

  return (
    <AnimatePresence>
      {active ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <motion.div
            className="mx-4 w-full max-w-md rounded-2xl border border-gray-200/60 bg-white p-8 shadow-xl"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: authEase }}
          >
            <div className="mb-6 h-1 overflow-hidden rounded-full bg-slate-100">
              <motion.div
                className="h-full origin-left rounded-full bg-blue-600"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: reduceMotion ? 0.3 : messages.length * 1.1, ease: authEase }}
              />
            </div>

            <svg className="mb-5 h-12 w-full" viewBox="0 0 200 40" aria-hidden>
              <motion.path
                d="M0 28 Q 50 8 100 22 T 200 18"
                fill="none"
                stroke="#2563eb"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0.3 }}
                animate={{ pathLength: 1, opacity: [0.35, 0.7, 0.35] }}
                transition={{
                  pathLength: { duration: 1.2, ease: authEase },
                  opacity: { duration: 2, repeat: Infinity },
                }}
              />
            </svg>

            <AnimatePresence mode="wait">
              <motion.p
                key={messages[index]}
                className="text-center text-sm font-medium text-slate-700"
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                {...(reduceMotion ? {} : { exit: { opacity: 0, y: -6 } })}
                transition={{ duration: 0.3 }}
              >
                {messages[index]}
              </motion.p>
            </AnimatePresence>

            <motion.div
              className="mt-4 flex justify-center gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="h-1 w-1 rounded-full bg-blue-500/50"
                  animate={reduceMotion ? {} : { opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
